/**
 * useTooltipAnimation
 *
 * Manages the 3-state animation machine for the tooltip card:
 *
 *   entering   → card is positioned off-screen above the viewport (opacity 0)
 *   visible    → card has slid into its final position (opacity 1)
 *   dismissing → card slides back off-screen (opacity 0); `onDismissed` fires
 *                when the CSS transition ends
 *
 * The double-rAF in triggerEnter guarantees the browser has painted the
 * off-screen position before we flip to 'visible', which is what triggers
 * the CSS slide-in transition.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { CSSProperties } from 'react';

export type AnimState = 'entering' | 'visible' | 'dismissing';

export type TooltipAnimationResult = {
  /** True while the card is animating in or out — use to place it off-screen. */
  isOffscreen: boolean;
  /** CSS transition string appropriate for the current animation direction. */
  transitionStyle: CSSProperties['transition'];
  /** Call when the user clicks the X button to start the dismiss animation. */
  startDismiss: () => void;
  /** Attach to the card's onTransitionEnd — fires `onDismissed` after exit. */
  handleTransitionEnd: () => void;
};

const TRANSITION_ENTER =
  'top 0.42s cubic-bezier(0.34, 1.56, 0.64, 1), left 0.42s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.25s ease-out';

const TRANSITION_EXIT =
  'top 0.22s ease-in, left 0.22s ease-in, opacity 0.18s ease-in';

export const useTooltipAnimation = (
  stepId: string,
  onDismissed: () => void,
): TooltipAnimationResult => {
  const [animState, setAnimState] = useState<AnimState>('entering');
  const rafRef = useRef<number>();
  const onDismissedRef = useRef(onDismissed);
  onDismissedRef.current = onDismissed;

  const triggerEnter = useCallback(() => {
    setAnimState('entering');
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = requestAnimationFrame(() => setAnimState('visible'));
    });
  }, []);

  useEffect(() => {
    triggerEnter();
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [stepId, triggerEnter]);

  const startDismiss = useCallback(() => setAnimState('dismissing'), []);

  const handleTransitionEnd = useCallback(() => {
    if (animState === 'dismissing') onDismissedRef.current();
  }, [animState]);

  const isOffscreen = animState === 'entering' || animState === 'dismissing';
  const transitionStyle =
    animState === 'dismissing' ? TRANSITION_EXIT : TRANSITION_ENTER;

  return { isOffscreen, transitionStyle, startDismiss, handleTransitionEnd };
};
